import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import warningIcon from "../../resources/icons/warning-info.svg";
import CreateOrganisationUserModal from "../../common/CreateOrganisationUserModal/CreateOrganisationUserModal";
import EntityConstants from "../../configs/EntityConstants";
import { ChevronRight } from "lucide-react";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import SimpleToggleButton from "../../common/SimpleToggleButton/SimpleToggleButton";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const TenantOrganisationPage = () => {
  const { tenant_code, organisation_code } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [limit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [updatingUserEmail, setUpdatingUserEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    user: null,
    currentStatus: false,
  });
  const [toggleRefresh, setToggleRefresh] = useState(false);
  const [isOrganisationActive, setIsOrganisationActive] = useState(false);
  const [organisationLoading, setOrganisationLoading] = useState(true);

  const isCreateUserDisabled = organisationLoading || !isOrganisationActive;

  const isTenantAdmin = authContext.userType?.toLowerCase() === "tenant_admin";

  const isTenantUser = authContext.userType?.toLowerCase() === "tenant_user";

  const performSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${
        EntityConstants.GET_ORGANISATION_USERS
      }?org_code=${encodeURIComponent(
        organisation_code,
      )}&limit=${limit}&offset=0&search=${encodeURIComponent(searchTerm)}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setNext(data.data.next);
        setPrevious(data.data.previous);
        setOffset(0);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_search_users"),
        });
        setUsers([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search users error:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_search_users"),
        });
        setUsers([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const fetchOrganisationStatus = async () => {
    if (!tenant_code || !organisation_code) return;

    setOrganisationLoading(true);
    try {
      const endpoint = `${EntityConstants.GET_TENANT_ORGANISATIONS}?tenant_code=${tenant_code}&limit=10&offset=0&search=${encodeURIComponent(organisation_code)}`;
      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        const results = Array.isArray(data.data?.results) ? data.data.results : [];
        const organisation = results.find(
          (org) => org.org_code === organisation_code,
        );
        setIsOrganisationActive(Boolean(organisation?.is_active));
      } else {
        setIsOrganisationActive(false);
      }
    } catch (error) {
      console.error("Error fetching organisation status:", error);
      setIsOrganisationActive(false);
    } finally {
      setOrganisationLoading(false);
    }
  };

  const fetchUsers = async (
    currentOffset = 0,
    search = "",
    isToggle = false,
  ) => {
    if (!isToggle) {
      setLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_ORGANISATION_USERS}?org_code=${organisation_code}&limit=${limit}&offset=${currentOffset}&search=${search}`
        : `${EntityConstants.GET_ORGANISATION_USERS}?org_code=${organisation_code}&limit=${limit}&offset=${currentOffset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setNext(data.data.next);
        setPrevious(data.data.previous);
        setOffset(currentOffset);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_fetch_organisation_users"),
        });
      }
    } catch (error) {
      console.error("Error fetching organisation users:", error);
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:failed_fetch_organisation_users"),
      });
    } finally {
      setLoading(false);
      if (isToggle) {
        setToggleRefresh(false);
      }
    }
  };

  useEffect(() => {
    if (tenant_code && organisation_code) {
      fetchOrganisationStatus();
      fetchUsers(0);
    }
  }, [tenant_code, organisation_code]);

  const handleUserToggle = (user) => {
    if (!isOrganisationActive) return;

    setConfirmModal({
      show: true,
      user: user,
      currentStatus: user.is_active,
    });
  };

  const confirmToggle = async () => {
    const { user, currentStatus } = confirmModal;
    if (!user) {
      cancelToggle();
      return;
    }

    setUpdatingUserEmail(user.email);
    try {
      const response = await POST(EntityConstants.POST_UPDATE_ORGANISATION, {
        org_code: organisation_code,
        email: user.email,
        status: !currentStatus,
      });

      const data = await response.json();

      if (data.success) {
        Toast({
          type: "success",
          title: t("tenant:success"),
          message: data.message || t("tenant:user_status_updated_successfully"),
        });
        setToggleRefresh(true);
        fetchUsers(offset, searchTerm, true);
      } else {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: data.message || t("tenant:failed_update_user_status"),
        });
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:failed_update_user_status"),
      });
    } finally {
      setUpdatingUserEmail(null);
      setConfirmModal({ show: false, user: null, currentStatus: false });
    }
  };

  const cancelToggle = () => {
    setConfirmModal({ show: false, user: null, currentStatus: false });
  };

  const handlePrevious = () => {
    if (previous !== null) {
      const prevOffset = Math.max(0, offset - limit);
      fetchUsers(prevOffset, searchTerm);
    }
  };

  const handleNext = () => {
    if (next !== null) {
      const nextOffset = offset + limit;
      fetchUsers(nextOffset, searchTerm);
    }
  };

  const handleUserCreated = () => {
    fetchUsers(offset, searchTerm);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setSearchTerm(value);
    setOffset(0);

    if (value.length > 0) {
      setIsSearching(true);
      handleSearch(value, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setUsers([]);
          setIsSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsSearching(false);
      fetchUsers(0, "");
    }
  };

  const handleTenantClick = () => {
    navigate("/issuer/tenant");
  };

  const handleAdminClick = () => {
    navigate(`/issuer/tenant/${tenant_code}/organisations/users`);
  };

  return (
    <div className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen" id={pageRootTestIds.tenantOrganisation}>
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {!(isTenantAdmin || isTenantUser) && (
                    <>
                      <button
                        onClick={handleTenantClick}
                        id="tenant-organisation-breadcrumb-tenant"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleTenantClick();
                          }
                        }}
                        className="hover:text-gray-800 cursor-pointer focus:outline-none"
                      >
                        {t("tenant:tenant_management")}
                      </button>
                      <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    </>
                  )}

                  <button
                    onClick={handleAdminClick}
                    id="tenant-organisation-breadcrumb-admin"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleAdminClick();
                      }
                    }}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {t("tenant:admin_management")}
                  </button>
                  <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                  <span className="text-gray-800 font-medium">
                    {t("tenant:organisation_users")}
                  </span>
                </div>
              </h2>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-3">
            <button
              onClick={() => {
                if (isCreateUserDisabled) return;
                setShowCreateUserModal(true);
              }}
              id="tenant-organisation-create-user"
              disabled={isCreateUserDisabled}
              className="px-4 py-2 bg-cfPrimaryButton text-white font-medium rounded-md transition-colors duration-200 hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-cfPrimaryButton"
            >
              {t("tenant:create_user")}
            </button>

            <input
              type="text"
              name="search"
              id="tenant-organisation-input-search"
              placeholder={t("tenant:search_for_users")}
              className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
              style={{
                width: "350px",
                fontSize: "0.875rem",
                lineHeight: "1.4rem",
              }}
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-4 px-4">
        <div className="overflow-x-auto">
          <div className="flex flex-col justify-center gap-2 min-w-full">
            <div className="flex bg-cfTableHeaderBackgroundColor items-center min-h-[4.5rem] border text-center min-w-[78.8rem] px-6 font-bold text-[14px]">
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:first_name")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:last_name")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:email")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:user_type")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:status")}
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                {t("tenant:action")}
              </div>
            </div>

            {isSearching && !toggleRefresh ? (
              <div className="flex justify-center items-center min-h-[8rem]">
                <CfLoader />
              </div>
            ) : loading ? (
              <div className="flex justify-center items-center min-h-[8rem]">
                <CfLoader />
              </div>
            ) : users.length === 0 ? (
              <div
                id="tenant-organisation-no-users"
                className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg"
              >
                {t("tenant:no_users_found")}
              </div>
            ) : (
              users.map((user) => (
                <div
                  key={user.email}
                  id={`tenant-organisation-user-row-${user.email}`}
                  className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem]"
                >
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
                      {user.first_name || t("tenant:na")}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">
                      {user.last_name || t("tenant:na")}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">{user.email}</p>
                  </div>
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">
                      {user.user_type}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[100px] flex-shrink-0 overflow-hidden">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                          : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
                      }`}
                    >
                      {user.is_active
                        ? t("tenant:active")
                        : t("tenant:inactive")}
                    </span>
                  </div>
                  <div
                    data-toggle-action
                    className={`w-1/6 text-center min-w-[100px] flex-shrink-0 overflow-hidden flex justify-center items-center ${
                      !isOrganisationActive || updatingUserEmail === user.email
                        ? "cursor-not-allowed"
                        : "cursor-default"
                    }`}
                  >
                    <SimpleToggleButton
                      checked={user.is_active}
                      onChange={() => handleUserToggle(user)}
                      disabled={
                        updatingUserEmail === user.email || !isOrganisationActive
                      }
                      testId={`tenant-organisation-toggle-${user.email}`}
                      ariaLabel={t("tenant:toggle_status_for", {
                        name: user.email,
                      })}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 w-full">
          <button
            onClick={handlePrevious}
            id="tenant-organisation-pagination-previous"
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={previous === null}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                />
              </svg>
              <span>{t("tenant:previous")}</span>
            </span>
          </button>

          <button
            onClick={handleNext}
            id="tenant-organisation-pagination-next"
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={next === null}
          >
            <span className="flex items-center justify-center gap-2">
              <span>{t("tenant:next")}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <CreateOrganisationUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
        tenantCode={tenant_code}
        orgCode={organisation_code}
      />

      <SimpleModal
        isOpen={confirmModal.show}
        closeModal={cancelToggle}
        successFunction={confirmToggle}
        modalIcon={warningIcon}
        modalHeaderMessage={`${
          confirmModal.currentStatus
            ? `${t("tenant:deactivate")}`
            : `${t("tenant:activate")}`
        } ${t("tenant:user")}`}
        modalBodyMessage={`${t("tenant:are_you_sure_want_to")} ${
          confirmModal.currentStatus
            ? t("tenant:deactivate_text")
            : t("tenant:activate_text")
        } "${confirmModal.user?.first_name} ${confirmModal.user?.last_name}"?`}
        leftButtonMessage={t("tenant:cancel")}
        rightButtonMessage={t("tenant:confirm")}
        testId="tenant-organisation-confirm-modal"
      />
    </div>
  );
};

export default TenantOrganisationPage;
